import { KatDropdown, KatInput, KatButton } from '@amzn/katal-react';
import React, { FC, FormEvent, useRef, useState } from 'react';

import { AssetState, LogoTextState } from 'src/types';
import { useSelection } from 'src/contexts/SelectionContext';
import { validateLogoTextState, LogoTextValidationErrors } from 'src/utils/validations/Logo';
import { trimLogoTextState } from 'src/utils/assetInput/assetInputUtils';
import { ErrorConstants } from 'src/constants';
import { LOGO_TEXT_INPUT_CONFIG, FieldConfig } from 'src/configs/logoTextInputConfig';

import styles from './LogoTextInput.module.css';

/**
 * Interface representing the props for the LogoTextInput component
 */
interface LogoTextInputProps {
  /** Current state of the asset */
  logoTextState: LogoTextState;
  /** Callback function to update the asset state */
  updateAssetState: (assetState: Partial<AssetState>) => void;
  /** Whether the component should be disabled */
  disabled?: boolean;
}

/**
 * Component for inputting logo text and related information
 * @param props - The component props
 * @param props.assetState - Current state of the asset
 * @param props.updateAssetState - Callback function to update the asset state
 */

export const LogoTextInput: FC<LogoTextInputProps> = ({
  logoTextState,
  updateAssetState,
  disabled = false,
}) => {
  const { marketplace } = useSelection();

  const [currentState, setCurrentState] = useState<LogoTextState>(logoTextState);
  const savedStateRef = useRef<LogoTextState>(logoTextState);
  const [fieldErrors, setFieldErrors] = useState<LogoTextValidationErrors>({});

  // Detect which fields have changed
  const fieldChanged = Object.keys(LOGO_TEXT_INPUT_CONFIG).reduce(
    (acc, key) => {
      const fieldKey = key as keyof LogoTextState;
      acc[fieldKey] = currentState[fieldKey] !== savedStateRef.current[fieldKey];
      return acc;
    },
    {} as Record<keyof LogoTextState, boolean>,
  );

  const hasChanges = Object.values(fieldChanged).some((changed) => changed);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const trimmedState = trimLogoTextState(currentState);
    const validationErrors = validateLogoTextState(trimmedState);

    setFieldErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    savedStateRef.current = trimmedState;
    setCurrentState(trimmedState);
    updateAssetState(trimmedState);
  };

  const handleCancel = () => {
    setCurrentState(savedStateRef.current);
    setFieldErrors({});
  };

  // Generic field change handler
  const handleFieldChange = <K extends keyof LogoTextState>(field: K, value: LogoTextState[K]) => {
    setCurrentState({ ...currentState, [field]: value });
    setFieldErrors((prev) => ({
      ...prev,
      [field]: ErrorConstants.MESSAGES.UNSAVED_CHANGES,
    }));
  };

  // Render a field based on its configuration
  const renderField = (fieldKey: keyof LogoTextState, config: FieldConfig) => {
    const value = currentState[fieldKey];
    const error = fieldErrors[fieldKey];

    const commonProps = {
      label: config.label,
      placeholder: config.placeholder,
      name: String(fieldKey),
      disabled,
      state: error ? ('error' as const) : undefined,
      'state-label': error,
      'data-testid': config.dataTestId,
    };

    switch (config.type) {
      case 'text':
        return (
          <KatInput
            key={fieldKey}
            {...commonProps}
            value={value as string}
            onInput={(e) =>
              handleFieldChange(fieldKey, e.currentTarget.value as LogoTextState[typeof fieldKey])
            }
            maxlength={config.maxLength}
          />
        );

      case 'dropdown':
        return (
          <KatDropdown
            key={fieldKey}
            {...commonProps}
            value={value as string}
            options={config.options(marketplace)}
            onChange={(event: KatDropdown.ChangeEvent) =>
              handleFieldChange(fieldKey, event.detail.value as LogoTextState[typeof fieldKey])
            }
          />
        );

      default:
        return null;
    }
  };

  return (
    <form className={styles.container} onSubmit={handleSubmit} data-testid="logo-text-form">
      {Object.entries(LOGO_TEXT_INPUT_CONFIG).map(([key, config]) =>
        renderField(key as keyof LogoTextState, config),
      )}
      <div className={styles.buttonContainer}>
        <KatButton
          type="button"
          label="Cancel"
          variant="tertiary"
          onClick={handleCancel}
          disabled={disabled || !hasChanges}
          data-testid="logo-text-cancel-button"
        />
        <KatButton
          type="submit"
          label="Save"
          disabled={disabled || !hasChanges}
          data-testid="logo-text-save-button"
        />
      </div>
    </form>
  );
};
